import { useState, useEffect } from 'react';
import { useToast } from '@/components/ui/use-toast';
import { useTenant } from '@/contexts/TenantContext';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { SlidePanel } from "@/components/ui/slide-panel";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from '@/components/ui/badge';
import { supabase } from '@/lib/supabaseClient';
import { getCurrentTenantId } from '@/utils/tenantUtils';
import { Plus, Pencil, Trash2, Sun, ClipboardList, Globe, Users, Copy, History, AlertTriangle } from 'lucide-react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { ChecklistsTab } from '@/components/settings/ChecklistsTab';
import { PipelineStagesManager } from '@/components/pipeline/PipelineStagesManager';

interface PipelineTemplate {
  id: string;
  name: string;
  description: string;
  pipeline_type: 'commercial' | 'residential' | 'internal';
  stages: any[];
  scope: 'global' | 'tenant';
  is_active: boolean;
  created_by: string;
  owner_tenant_id: string;
  created_at: string;
  updated_at: string;
  template_version?: number;
}

interface TemplateVersion {
  id: string;
  template_id: string;
  version_number: number;
  snapshot_data: any;
  created_at: string;
  tenant_id: string;
}

interface TemplateFormData {
  name: string;
  description: string;
  pipeline_type: 'commercial' | 'residential' | 'internal';
  scope: 'global' | 'tenant';
}

const PIPELINE_TYPES = [
  { value: 'residential', label: 'Residential' },
  { value: 'commercial', label: 'Commercial' },
  { value: 'internal', label: 'Internal' }
];

export function PipelineTemplates() {
  const { toast } = useToast();
  const { currentTenant } = useTenant();
  const { user } = useAuth();
  const [templates, setTemplates] = useState<PipelineTemplate[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<PipelineTemplate | null>(null);
  const [isUserSuperadmin, setIsUserSuperadmin] = useState(false);
  const [formData, setFormData] = useState<TemplateFormData>({
    name: '',
    description: '',
    pipeline_type: 'residential',
    scope: 'tenant'
  });

  // Dialog states for template management
  const [selectedTemplateForChecklists, setSelectedTemplateForChecklists] = useState<PipelineTemplate | null>(null);
  const [isChecklistDialogOpen, setIsChecklistDialogOpen] = useState(false);

  // Version history states
  const [selectedTemplateForVersions, setSelectedTemplateForVersions] = useState<PipelineTemplate | null>(null);
  const [isVersionHistoryOpen, setIsVersionHistoryOpen] = useState(false);
  const [templateVersions, setTemplateVersions] = useState<TemplateVersion[]>([]);
  const [isLoadingVersions, setIsLoadingVersions] = useState(false);
  const [selectedVersionToRollback, setSelectedVersionToRollback] = useState<TemplateVersion | null>(null);

  useEffect(() => {
    fetchTemplates();
    checkUserRole();
  }, [user]);

  const checkUserRole = async () => {
    if (!user) {
      setIsUserSuperadmin(false);
      return;
    }

    try {
      // Check if user is superadmin by looking at their roles
      const { data: userData } = await supabase
        .from('users')
        .select(`
          role_id,
          roles!inner (
            name
          )
        `)
        .eq('id', user.id)
        .single();

      const userRole = Array.isArray(userData?.roles) ? userData.roles[0] : userData?.roles;
      const isSuperadmin = userRole?.name === 'Superadmin' || userData?.role_id === 42;

      setIsUserSuperadmin(isSuperadmin);
    } catch (error) {
      console.error('Error checking user role:', error);
      setIsUserSuperadmin(false);
    }
  };

  const fetchTemplates = async () => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from('pipeline_templates_library')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setTemplates(data || []);
    } catch (error) {
      console.error('Error fetching templates:', error);
      toast({
        title: "Error",
        description: "Failed to fetch pipeline templates",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenDialog = (template?: PipelineTemplate) => {
    if (template) {
      setEditingTemplate(template);
      setFormData({
        name: template.name,
        description: template.description,
        pipeline_type: template.pipeline_type,
        scope: template.scope
      });
    } else {
      setEditingTemplate(null);
      setFormData({
        name: '',
        description: '',
        pipeline_type: 'residential',
        scope: 'tenant'
      });
    }
    setIsDialogOpen(true);
  };

  const handleSaveTemplate = async () => {
    if (!formData.name.trim()) {
      toast({
        title: "Error",
        description: "Template name is required",
        variant: "destructive"
      });
      return;
    }

    try {
      const tenantId = await getCurrentTenantId();

      if (editingTemplate) {
        // Update existing template
        const { error } = await supabase
          .from('pipeline_templates_library')
          .update({
            name: formData.name,
            description: formData.description,
            pipeline_type: formData.pipeline_type,
            scope: formData.scope,
            updated_at: new Date().toISOString()
          })
          .eq('id', editingTemplate.id);

        if (error) throw error;
      } else {
        // Create new template
        if (!user?.id) {
          toast({
            title: "Error",
            description: "User not authenticated",
            variant: "destructive"
          });
          return;
        }

        const { error } = await supabase
          .from('pipeline_templates_library')
          .insert({
            name: formData.name,
            description: formData.description,
            pipeline_type: formData.pipeline_type,
            scope: formData.scope,
            stages: [],
            is_active: true,
            created_by: user.id,
            owner_tenant_id: tenantId,
            template_version: 1
          });

        if (error) throw error;
      }

      toast({
        title: "Success",
        description: `Template ${editingTemplate ? 'updated' : 'created'} successfully`
      });

      setIsDialogOpen(false);
      fetchTemplates();
    } catch (error) {
      console.error('Error saving template:', error);
      toast({
        title: "Error",
        description: `Failed to ${editingTemplate ? 'update' : 'create'} template`,
        variant: "destructive"
      });
    }
  };

  const handleDeleteTemplate = async (template: PipelineTemplate) => {
    if (!confirm(`Are you sure you want to delete "${template.name}"?`)) return;

    try {
      const { error } = await supabase
        .from('pipeline_templates_library')
        .delete()
        .eq('id', template.id);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Template deleted successfully"
      });

      fetchTemplates();
    } catch (error) {
      console.error('Error deleting template:', error);
      toast({
        title: "Error",
        description: "Failed to delete template",
        variant: "destructive"
      });
    }
  };

  const handleDuplicateTemplate = async (template: PipelineTemplate) => {
    try {
      const tenantId = await getCurrentTenantId();

      if (!user?.id) {
        toast({
          title: "Error",
          description: "User not authenticated",
          variant: "destructive"
        });
        return;
      }

      const { error } = await supabase
        .from('pipeline_templates_library')
        .insert({
          name: `${template.name} (Copy)`,
          description: template.description,
          pipeline_type: template.pipeline_type,
          scope: 'tenant', // Always create copies as tenant-scoped
          stages: template.stages,
          is_active: true,
          created_by: user.id,
          owner_tenant_id: tenantId,
          template_version: 1
        });

      if (error) throw error;

      toast({
        title: "Success",
        description: "Template duplicated successfully"
      });

      fetchTemplates();
    } catch (error) {
      console.error('Error duplicating template:', error);
      toast({
        title: "Error",
        description: "Failed to duplicate template",
        variant: "destructive"
      });
    }
  };

  const handleOpenChecklistDialog = (template: PipelineTemplate) => {
    setSelectedTemplateForChecklists(template);
    setIsChecklistDialogOpen(true);
  };

  const handleUpdateTemplateStages = async (templateId: string, updatedStages: any[]) => {
    try {
      const { error } = await supabase
        .from('pipeline_templates_library')
        .update({
          stages: updatedStages,
          updated_at: new Date().toISOString()
        })
        .eq('id', templateId);

      if (error) throw error;

      // Refresh templates to show updated stages
      await fetchTemplates();
    } catch (error) {
      console.error('Error updating template stages:', error);
      throw error; // Re-throw to let PipelineStagesManager handle the error toast
    }
  };

  // Fetch version history for a template
  const fetchVersionHistory = async (templateId: string) => {
    setIsLoadingVersions(true);
    try {
      const { data, error } = await supabase
        .from('pipeline_template_versions')
        .select('*')
        .eq('template_id', templateId)
        .order('created_at', { ascending: false })
        .limit(5);

      if (error) throw error;

      setTemplateVersions(data || []);
    } catch (error) {
      console.error('Error fetching version history:', error);
      toast({
        title: "Error",
        description: "Failed to load version history",
        variant: "destructive"
      });
    } finally {
      setIsLoadingVersions(false);
    }
  };

  // Open version history dialog
  const handleOpenVersionHistory = (template: PipelineTemplate) => {
    setSelectedTemplateForVersions(template);
    setIsVersionHistoryOpen(true);
    fetchVersionHistory(template.id);
  };

  // Rollback to a previous version
  const handleConfirmRollback = async () => {
    if (!selectedTemplateForVersions || !selectedVersionToRollback) return;

    try {
      // Call the database function to rollback
      const { data, error } = await supabase.rpc('rollback_template_to_version', {
        p_template_id: selectedTemplateForVersions.id,
        p_version_id: selectedVersionToRollback.id
      });

      if (error) throw error;

      if (data && data.success === false) {
        throw new Error(data.error || 'Rollback failed');
      }

      toast({
        title: "Success",
        description: `Template rolled back to version ${data.restored_version}`,
      });

      // Refresh templates and version history
      await fetchTemplates();
      await fetchVersionHistory(selectedTemplateForVersions.id);

      setSelectedVersionToRollback(null);
    } catch (error: any) {
      console.error('Error rolling back template:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to rollback template",
        variant: "destructive"
      });
    }
  };


  const filteredTemplates = templates.filter(template => {
    // Show all templates to superadmins, only relevant templates to regular users
    if (isUserSuperadmin) return true;

    // For regular users, show:
    // 1. Global templates (available to all tenants)
    // 2. Local templates that belong to their current tenant
    if (template.scope === 'global') return true;
    return template.scope === 'tenant' && template.owner_tenant_id === currentTenant?.tenant_id;
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-lg font-semibold">Pipeline Templates</h2>
          <p className="text-sm text-muted-foreground">
            Create and manage reusable pipeline templates
          </p>
        </div>
        <Button onClick={() => handleOpenDialog()}>
          <Plus className="w-4 h-4 mr-2" />
          New Template
        </Button>
      </div>

      {/* Templates Table */}
      {isLoading ? (
        <div className="flex justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      ) : (
        <div className="border rounded-lg">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Scope</TableHead>
                <TableHead>Stages</TableHead>
                <TableHead>Created</TableHead>
                <TableHead className="w-32">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredTemplates.map((template) => (
                <TableRow key={template.id}>
                  <TableCell>
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="font-medium">{template.name}</p>
                        {/* Only show legacy badge for templates without stages array (null/undefined), not empty arrays */}
                        {template.stages === null || template.stages === undefined ? (
                          <span className="text-xs bg-orange-100 text-orange-700 px-2 py-1 rounded-md border border-orange-200">
                            Legacy
                          </span>
                        ) : null}
                      </div>
                      {template.description && (
                        <p className="text-sm text-muted-foreground">{template.description}</p>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline">
                      {PIPELINE_TYPES.find(t => t.value === template.pipeline_type)?.label || template.pipeline_type}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Badge variant={template.scope === 'global' ? 'default' : 'secondary'}>
                      {template.scope === 'global' ? (
                        <>
                          <Globe className="w-3 h-3 mr-1" />
                          Global
                        </>
                      ) : (
                        <>
                          <Users className="w-3 h-3 mr-1" />
                          Local
                        </>
                      )}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <span className="text-sm">{template.stages?.length || 0} stages</span>
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {new Date(template.created_at).toLocaleDateString()}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleOpenChecklistDialog(template)}
                            className="text-muted-foreground hover:text-foreground"
                          >
                            <ClipboardList className="w-4 h-4" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>Manage Checklists</TooltipContent>
                      </Tooltip>

                      <PipelineStagesManager
                        pipelineName={template.name}
                        icon="sun"
                        templateMode={true}
                        templateId={template.id}
                        templateStages={template.stages || []}
                        onTemplateStagesUpdate={async (updatedStages) => {
                          await handleUpdateTemplateStages(template.id, updatedStages);
                        }}
                      />

                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleOpenDialog(template)}
                            className="text-muted-foreground hover:text-foreground"
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>Edit Template</TooltipContent>
                      </Tooltip>

                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDuplicateTemplate(template)}
                            className="text-muted-foreground hover:text-foreground"
                          >
                            <Copy className="w-4 h-4" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>Duplicate Template</TooltipContent>
                      </Tooltip>

                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleOpenVersionHistory(template)}
                            className="text-muted-foreground hover:text-foreground"
                          >
                            <History className="w-4 h-4" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>Version History</TooltipContent>
                      </Tooltip>

                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDeleteTemplate(template)}
                        className="text-muted-foreground hover:text-destructive"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          {filteredTemplates.length === 0 && (
            <div className="text-center py-8 text-muted-foreground">
              No pipeline templates found. Create your first template to get started.
            </div>
          )}
        </div>
      )}

      {/* Create/Edit Template Dialog */}
      <SlidePanel
        isOpen={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        title={editingTemplate ? 'Edit Template' : 'Create New Template'}
        width="xl"
        topOffset="4.5rem"
        showEdgeCloseButton={true}
        footer={
          <div className="flex justify-end space-x-2">
            <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveTemplate}>
              {editingTemplate ? 'Update Template' : 'Create Template'}
            </Button>
          </div>
        }
      >
        <div className="space-y-4">
          <div>
            <Label htmlFor="name">Template Name *</Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              placeholder="Enter template name"
            />
          </div>

          <div>
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Enter template description"
              rows={3}
            />
          </div>

          <div>
            <Label htmlFor="pipeline_type">Pipeline Type *</Label>
            <Select
              value={formData.pipeline_type}
              onValueChange={(value) => setFormData(prev => ({ ...prev, pipeline_type: value as any }))}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select pipeline type" />
              </SelectTrigger>
              <SelectContent>
                {PIPELINE_TYPES.map(type => (
                  <SelectItem key={type.value} value={type.value}>
                    {type.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {isUserSuperadmin && (
            <div>
              <Label htmlFor="scope">Template Scope *</Label>
              <Select
                value={formData.scope}
                onValueChange={(value) => setFormData(prev => ({ ...prev, scope: value as any }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select template scope" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="tenant">
                    <div className="flex items-center">
                      <Users className="w-4 h-4 mr-2" />
                      Local (Current Tenant)
                    </div>
                  </SelectItem>
                  <SelectItem value="global">
                    <div className="flex items-center">
                      <Globe className="w-4 h-4 mr-2" />
                      Global (All Tenants)
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground mt-1">
                Global templates are available to all tenants. Local templates are only available to your tenant.
              </p>
            </div>
          )}
        </div>
      </SlidePanel>

      {/* Checklist Management Dialog */}
      <SlidePanel
        isOpen={isChecklistDialogOpen}
        onClose={() => setIsChecklistDialogOpen(false)}
        title="Manage Template Checklists"
        width="2xl"
        topOffset="4.5rem"
        showEdgeCloseButton={true}
      >
        <div className="text-sm text-muted-foreground mb-6">
          Create and manage checklist templates for this pipeline template.
        </div>
        <div className="py-4 overflow-y-auto flex-1">
          {selectedTemplateForChecklists && (
            <ChecklistsTab
              pipelineType={selectedTemplateForChecklists.pipeline_type}
              templateMode={true}
              templateId={selectedTemplateForChecklists.id}
              onRefreshData={() => {
                // Refresh data if needed
              }}
            />
          )}
        </div>
      </SlidePanel>

      {/* Version History Dialog */}
      <SlidePanel
        isOpen={isVersionHistoryOpen}
        onClose={() => setIsVersionHistoryOpen(false)}
        title={`Version History: ${selectedTemplateForVersions?.name || ''}`}
        width="xl"
        topOffset="4.5rem"
      >
        <div className="space-y-4">
          {isLoadingVersions ? (
            <div className="text-center py-8 text-muted-foreground">
              Loading version history...
            </div>
          ) : templateVersions.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No previous versions available. Changes to this template will be saved here.
            </div>
          ) : selectedVersionToRollback ? (
            // Confirmation view
            <div className="space-y-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSelectedVersionToRollback(null)}
                className="mb-2"
              >
                ← Back to versions
              </Button>

              <div className="border-2 border-primary rounded-lg p-4 space-y-4">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-6 h-6 text-primary mt-1" />
                  <div className="flex-1">
                    <h3 className="font-semibold text-lg mb-2">
                      Confirm Rollback to Version {selectedVersionToRollback.version_number}
                    </h3>
                    <p className="text-sm text-muted-foreground mb-4">
                      This will restore the template to its previous state. The current configuration will be saved as a new version.
                    </p>

                    <div className="bg-muted p-3 rounded-md space-y-2 mb-4">
                      <p className="text-sm font-medium">What will be restored:</p>
                      <ul className="text-sm text-muted-foreground space-y-1 ml-4 list-disc">
                        <li>Pipeline type: {selectedVersionToRollback.snapshot_data.pipeline_type}</li>
                        <li>{selectedVersionToRollback.snapshot_data.stages?.length || 0} stages</li>
                        <li>{selectedVersionToRollback.snapshot_data.checklists?.length || 0} checklists with all items</li>
                        <li>Saved on: {new Date(selectedVersionToRollback.created_at).toLocaleString()}</li>
                      </ul>
                    </div>

                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        onClick={() => setSelectedVersionToRollback(null)}
                      >
                        Cancel
                      </Button>
                      <Button
                        variant="default"
                        onClick={handleConfirmRollback}
                      >
                        Confirm Rollback
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            // Version list view
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">
                You can rollback to any of the previous 5 versions. This will restore the template configuration, stages, and all checklists.
              </p>

              {templateVersions.map((version, index) => (
                <div
                  key={version.id}
                  className="border rounded-lg p-4 hover:border-primary/50 transition-colors"
                >
                  <div className="flex items-start justify-between">
                    <div className="space-y-2 flex-1">
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold">Version {version.version_number}</h3>
                        <Badge variant="outline" className="text-xs">
                          {index === 0 ? 'Most Recent' : index === 1 ? 'Previous' : `${index + 1} versions ago`}
                        </Badge>
                      </div>

                      <div className="text-sm text-muted-foreground space-y-1">
                        <p>
                          <strong>Saved:</strong>{' '}
                          {new Date(version.created_at).toLocaleString()}
                        </p>
                        <p>
                          <strong>Type:</strong>{' '}
                          {version.snapshot_data.pipeline_type}
                        </p>
                        <p>
                          <strong>Stages:</strong>{' '}
                          {version.snapshot_data.stages?.length || 0}
                        </p>
                        <p>
                          <strong>Checklists:</strong>{' '}
                          {version.snapshot_data.checklists?.length || 0}
                        </p>
                      </div>
                    </div>

                    <Button
                      variant="default"
                      size="sm"
                      onClick={() => setSelectedVersionToRollback(version)}
                    >
                      Rollback
                    </Button>
                  </div>
                </div>
              ))}

              <div className="mt-4 p-3 bg-muted/50 rounded-lg">
                <p className="text-xs text-muted-foreground">
                  <strong>Note:</strong> Only the last 5 versions are kept. Rolling back will create a new version from the current state.
                </p>
              </div>
            </div>
          )}
        </div>
      </SlidePanel>

    </div>
  );
}